import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button } from "@shared/ui/button";
import {
  ArrowLeft,
  MapPin,
  Edit,
  Plus,
  Download,
  Ban,
  Calendar,
  User,
  Repeat,
} from "lucide-react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import AddEntityModal from "../../add/AddEntityModal";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { getStatusBadgeClass } from "@shared/utils/statusUtils";
import {
  useTerritoryPremiseStats,
  usePatchConfigMap,
} from "@features/onboarding/utility-setup/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { Tabs } from "@shared/ui/tabs";
import UnitDetail from "./tabsComponents/unitDetail/unitDetail";
import Summary from "./tabsComponents/summary/summary";
import Resource from "./tabsComponents/resource/resource";
import Consumers from "./tabsComponents/consumers/consumers";
import { TimelineTab } from "@features/cx/shared/components/tabs/TimelineTab";

interface UnitDetailViewProps {
  result?: any;
  entityData?: any;
  onBack?: () => void;
}

export const UnitDetailView: React.FC<UnitDetailViewProps> = ({
  result,
  entityData: propEntityData,
  onBack,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const [searchParams, setSearchParams] = useSearchParams();
  const { code } = useParams<{ code: string }>();
  const decodedCode = decodeURIComponent(code || "");
  const tab = decodeURIComponent(searchParams.get("tab") || "unit");

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const patchConfigMapMutation = usePatchConfigMap();

  // Get entity data from location state or props
  const { data: entityData } = location.state || {};
  const resultEntityData = result;
  const initialEntity = propEntityData || entityData || resultEntityData || {};

  const [entityCode, setEntityCode] = useState<string>(() => {
    return (
      initialEntity?.code ||
      initialEntity?.configurationCode ||
      decodedCode ||
      "N/A"
    );
  });

  useEffect(() => {
    const newCode =
      propEntityData?.code || entityData?.code || result?.code || decodedCode;
    if (newCode && newCode !== entityCode) {
      setEntityCode(newCode);
    }
  }, [
    propEntityData?.code,
    entityData?.code,
    result?.code,
    decodedCode,
    entityCode,
  ]);

  const { data: statsData } = useTerritoryPremiseStats(entityCode);

  const entityName = statsData?.basicInfo?.name || "N/A";
  const configurationCode = statsData?.basicInfo?.configurationCode || "N/A";
  const isActive =
    statsData?.basicInfo?.isActive !== undefined
      ? statsData.basicInfo.isActive
      : initialEntity?.isActive !== false;
  const createdDate = statsData?.basicInfo?.createdDate || "N/A";
  const createdBy = statsData?.basicInfo?.createdBy || "N/A";
  const updatedDate = statsData?.basicInfo?.updatedOn || "N/A";
  const updatedBy = statsData?.basicInfo?.updatedBy || "N/A";
  const parentName = statsData?.parentInfo?.name || "N/A";
  const parcelId = statsData?.parentInfo?.parcelId || "N/A";
  const unitType = statsData?.basicInfo?.unitType || "N/A";
  const description = (statsData?.basicInfo as any)?.description || "N/A";

  const handleToggleStatus = () => {
    patchConfigMapMutation.mutate({
      mapType: "TERRITORY_JSON",
      code: entityCode,
      is_active: !isActive,
      remote_utility_id: remoteUtilityId,
    });
  };

  const handleBackClick = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(`/utility/utility-setup/service-area?tab=unit`);
    }
  };

  const detailFields = useMemo(
    () => [
      {
        label: "Entity Type",
        value: (
          <span className="font-medium inline-flex items-center gap-1">
            Unit
          </span>
        ),
      },
      {
        label: "Name",
        value: (
          <span className="font-medium inline-flex items-center gap-1">
            {entityName}
          </span>
        ),
      },
      {
        label: "Code",
        value: (
          <span className="font-mono text-xs bg-gray-100 border border-gray-200 text-gray-800 px-2 py-0.5 rounded font-medium">
            {configurationCode}
          </span>
        ),
      },
      {
        label: "Status",
        value: (
          <span
            className={`text-xs font-semibold px-2.5 py-0.5 rounded-full inline-flex items-center gap-1 ${getStatusBadgeClass(
              isActive ? "Active" : "Inactive",
            )}`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                isActive ? "bg-emerald-500" : "bg-red-500"
              }`}
            />
            {isActive ? "Active" : "Inactive"}
          </span>
        ),
      },
      {
        label: "Parcel ID",
        value: (
          <span className="font-mono text-xs bg-gray-50 border border-gray-200 text-gray-800 px-2 py-0.5 rounded font-medium">
            {parcelId}
          </span>
        ),
      },
      {
        label: "Created On",
        value: (
          <span className="text-gray-700 font-medium inline-flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5 text-gray-400" />
            {createdDate}
          </span>
        ),
      },
      {
        label: "Created By",
        value: createdBy,
      },
      {
        label: "Updated On",
        value: updatedDate,
      },
      {
        label: "Updated By",
        value: updatedBy,
      },
    ],
    [entityName, configurationCode, isActive, parcelId, createdDate, createdBy],
  );

  const tabComponents = {
    summary: {
      label: "Summary",
      component: <Summary />,
    },
    unitDetail: {
      label: "Unit Detail",
      component: <UnitDetail data={statsData} />,
    },
    consumers: {
      label: "Consumers",
      component: <Consumers />,
    },
    resource: {
      label: "Resource",
      component: <Resource />,
    },
    activityLog: {
      label: "Activity Log",
      component: (
        <TimelineTab
          remoteUtilityId={Number(remoteUtilityId) || 1}
          keyCode={entityCode || ""}
          limit={10}
        />
      ),
    },
  };

  const urlMapping = {
    summary: "summary",
    unitDetail: "unit-detail",
    consumers: "consumers",
    resource: "resource",
    activityLog: "activity-log",
  };

  return (
    <div className="space-y-6 p-2">
      {/* Top Navigation & Header */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <button
            onClick={handleBackClick}
            className="p-1.5 rounded-lg text-gray-600 hover:bg-gray-200/60 transition-colors cursor-pointer"
            title="Go Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex flex-col">
            <h1 className="text-xl font-bold text-gray-900 tracking-tight">
              Service Area Details
            </h1>
            <p className="text-xs text-gray-500">
              View detailed information and hierarchy for {entityName}
            </p>
          </div>
        </div>

        {/* Entity Banner & Quick Actions Section */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Main Info Card (3 Cols) */}
          <div className="lg:col-span-3 bg-white rounded-xl border border-gray-200/80 p-5 shadow-xs space-y-5">
            {/* Header info row */}
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 shrink-0 mt-0.5">
                <MapPin className="w-5 h-5" />
              </div>
              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-2.5">
                  <h2 className="text-xl font-bold text-gray-900 tracking-tight">
                    {entityName}
                  </h2>
                  <span className="font-mono text-xs text-gray-700 bg-gray-100 border border-gray-200 px-2.5 py-0.5 rounded font-medium">
                    {configurationCode}
                  </span>
                  <span
                    className={`text-xs font-semibold px-2.5 py-0.5 rounded-full inline-flex items-center gap-1.5 ${getStatusBadgeClass(
                      isActive ? "Active" : "Inactive",
                    )}`}
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${
                        isActive ? "bg-emerald-500" : "bg-red-500"
                      }`}
                    />
                    {isActive ? "Active" : "Inactive"}
                  </span>
                  <span className="text-xs text-gray-600 bg-gray-50 border border-gray-200 px-2.5 py-0.5 rounded font-medium">
                    Unit
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 pt-0.5">
                  <span className="inline-flex items-center gap-1">
                    <User className="w-3.5 h-3.5 text-gray-400" />
                    {createdBy}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5 text-gray-400" />
                    Created {createdDate}
                  </span>
                  {parentName && (
                    <span className="inline-flex items-center gap-1 text-blue-600 font-medium">
                      <Repeat className="w-3.5 h-3.5 text-blue-500" />
                      Under {parentName}
                    </span>
                  )}
                  {unitType && (
                    <span className="text-xs text-gray-700 bg-gray-100 border border-gray-200 px-2 py-0.5 rounded font-medium">
                      {unitType}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Key-Value Summary Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-y-4 gap-x-6 pt-3 border-t border-gray-100 text-xs">
              {detailFields.map((field, idx) => (
                <ValueLabel key={idx} label={field.label} value={field.value} />
              ))}
            </div>
            <div className="pt-3 text-xs">
              <ValueLabel
                label="Description"
                value={<span className="text-gray-700">{description}</span>}
              />
            </div>
          </div>

          {/* Quick Actions Card (1 Col) */}
          <div className="bg-white rounded-xl border border-gray-200/80 p-5 shadow-xs flex flex-col space-y-3">
            <h3 className="text-sm font-bold text-gray-800 tracking-wide">
              Quick Actions
            </h3>
            <div className="space-y-2">
              <Button
                variant="outline"
                onClick={() => setIsEditModalOpen(true)}
                className="w-full justify-start"
              >
                <Edit className="w-3.5 h-3.5 mr-2 text-gray-500" />
                Edit
              </Button>

              <Button
                onClick={() => setIsAddModalOpen(true)}
                className="w-full justify-start"
              >
                <Plus className="w-3.5 h-3.5 mr-2" />
                Add Entity
              </Button>

              <Button variant="outline" className="w-full justify-start">
                <Download className="w-3.5 h-3.5 mr-2 text-gray-500" />
                Export Data
              </Button>

              <Button
                variant="outline"
                onClick={handleToggleStatus}
                isLoading={patchConfigMapMutation.isPending}
                className="w-full justify-start cursor-pointer"
              >
                <Ban className="w-3.5 h-3.5 mr-2 text-orange-500" />
                {isActive ? "Mark Inactive" : "Mark Active"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        defaultValue="summary"
        className="w-full"
        tabsListClassName="grid grid-cols-5 w-full"
        urlMapping={urlMapping}
        tabComponents={tabComponents}
        level={0}
      />

      {/* Edit Modal */}
      {isEditModalOpen && (
        <AddEntityModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          editData={statsData || initialEntity}
          editMode={true}
          entityType="unit"
        />
      )}

      {/* Add Entity Modal */}
      {isAddModalOpen && (
        <AddEntityModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          entityType="unit"
        />
      )}
    </div>
  );
};

export default UnitDetailView;
